import * as Sentry from '@sentry/react'
import ConfirmationDialog from 'components/ReusableComponents/ConfirmationDialog'
import Dialog from 'components/ReusableComponents/Dialog'
import LoadingIndicator from 'components/ReusableComponents/LoadingIndicator'
import Table, {
  Cell,
  Row,
  TitleCell,
  TitleRow
} from 'components/ReusableComponents/Table'
import { transactionStatusTypes } from 'features/BookingDetails/types'
import BookingDetailsDialog from 'features/BookingDetailsDialog'
import useFirstRender from 'hooks/useFirstRender'
import useLogzIo from 'hooks/useLogzIo'
import usePlausible from 'hooks/usePlausible'
import moment from 'moment'
import { useEffect, useState } from 'react'
import subscribersApi from 'services/api/subscribers'
import { formatPrice } from 'utils/common'

import styles from './BookingDetails.module.scss'
import GuestDetails from './GuestDetails'

function getStatusColor(status) {
  const activeColor = '#428BE1'
  const goodColor = '#66bb6a'
  const badColor = '#E45A5A'

  switch (transactionStatusTypes[status]) {
    case transactionStatusTypes.Cancelled:
      return badColor

    case transactionStatusTypes.NoShow:
      return badColor

    case transactionStatusTypes.InUse:
      return activeColor

    case transactionStatusTypes.Completed:
      return goodColor

    case transactionStatusTypes.Confirmed:
      return goodColor

    default:
      return '#b1aeae'
  }
}

const BookingHistory = ({ subscriberId, onClose, onBack }) => {
  const [bookings, setBookings] = useState()
  const [guest, setGuest] = useState()

  const [selectedBookingId, setSelectedBookingId] = useState()

  const [isLoading, setIsLoading] = useState(true)

  const [isErred, setIsErred] = useState(false)

  const log = useLogzIo()
  const [sortAscending, setSortAscending] = useState(false)
  const [sortIndex, setSortIndex] = useState(0)
  const { postEvent, plausibleEvents } = usePlausible()

  const openDetails = id => {
    setSelectedBookingId(id)
  }
  useFirstRender(() => {
    postEvent(plausibleEvents.ViewGuestProfile, {
      subscriberId
    })
  }, [postEvent, plausibleEvents, subscriberId])

  function handleTitleClick(i) {
    const sortableIndexes = [0, 1, 2, 3, 4, 5, 6]

    if (sortIndex === i) {
      setSortAscending(d => !d)
    }

    if (sortableIndexes.includes(i)) {
      setSortIndex(i)
    }
  }

  function sortResults(results) {
    const sortedData = results.sort((a, b) => {
      switch (sortIndex) {
        case 0:
          return new Date(a.bookingDate) - new Date(b.bookingDate)

        case 1:
          return new Date(a.checkIn) - new Date(b.checkIn)

        case 2:
          return new Date(a.checkOut) - new Date(b.checkOut)

        case 3:
          return a.adults + a.children - (b.adults + b.children)

        case 4:
          if (a.agencyParentName === null) return -1

          if (a.agencyParentName === b.agencyParentName) return 0

          return a.agencyParentName - b.agencyParentName

        case 5:
          return a.totalRate - b.totalRate

        case 6:
          if (transactionStatusTypes[a.status] === transactionStatusTypes[b.status])
            return 0

          return transactionStatusTypes[a.status] > transactionStatusTypes[b.status]
            ? 1
            : -1

        default:
          return 0
      }
    })

    return sortAscending ? sortedData : [...sortedData].reverse()
  }

  useEffect(() => {
    Promise.all([
      subscribersApi.getBookings({ subscriberId }),
      subscribersApi.find({ subscriberId })
    ])
      .then(responses => {
        const [bookingsRes, guestRes] = responses
        setBookings(
          bookingsRes.sort((a, b) => new Date(b.bookingDate) - new Date(a.bookingDate))
        )
        setGuest(Object.values(guestRes.subscribersFound)[0])
      })
      .catch(error => {
        Sentry.captureException(error)
        log({ level: 'Error', message: error.toString() })
        setIsErred(true)
      })
      .finally(() => {
        setIsLoading(false)
      }) // eslint-disable-next-line
  }, [])

  if (isLoading) {
    return (
      <Dialog>
        <LoadingIndicator white />
      </Dialog>
    )
  }

  if (isErred) {
    return (
      <ConfirmationDialog title="Something went wrong" onClose={onClose}>
        <p>Something while getting reservation details.</p>
        <p>This issue is automatically reported to our technical team</p>
      </ConfirmationDialog>
    )
  }

  return (
    <>
      {selectedBookingId && (
        <BookingDetailsDialog
          bookingId={selectedBookingId}
          onClose={onClose}
          onBack={() => setSelectedBookingId(null)}
        />
      )}
      <ConfirmationDialog
        theme="blacklist"
        title={`Booking history of ${guest.fullName}`}
        hasConfirmButton={false}
        maxWidth={1150}
        onClose={onClose}
        onBack={onBack}
      >
        <div className={styles.guestProfileWrapper}>
          <div className={styles.reservation}>
            <Table
              widths={[110, 70, 80, 58, 100, 85, 90, 90]}
              handleTitleCellClick={handleTitleClick}
              sortIndex={sortIndex}
              sortDirection={sortAscending ? 'up' : 'down'}
            >
              <TitleRow>
                <TitleCell>Booked on</TitleCell>
                <TitleCell>Check-in</TitleCell>
                <TitleCell>Check-out</TitleCell>
                <TitleCell>Guests</TitleCell>
                <TitleCell>Agency</TitleCell>
                <TitleCell>Total rate</TitleCell>
                <TitleCell>Status</TitleCell>
              </TitleRow>
              {bookings.length === 0 && (
                <p className={styles.noResults}>No bookings in 3 years</p>
              )}
              {sortResults(bookings).map(b => (
                <Row key={b.subscriberHotelBookingId}>
                  <Cell className={styles.cell}>
                    <span className={styles.darkCell}>
                      {moment(b.bookingDate).format('MMM DD, YYYY')}
                    </span>
                  </Cell>
                  <Cell className={styles.cell}>
                    {moment(b.checkIn).format('MMM DD')}
                  </Cell>
                  <Cell className={styles.cell}>
                    {moment(b.checkOut).format('MMM DD')}
                  </Cell>
                  <Cell className={styles.cell}>{b.adults + b.children}</Cell>
                  <Cell className={styles.cell}>
                    <div
                      data-tip={b.agencyParentName}
                      title={b.agencyParentName}
                      style={{
                        maxWidth: 80,
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        whiteSpace: 'nowrap',
                        display: 'block'
                      }}
                    >
                      {b.agencyParentName}
                    </div>
                  </Cell>
                  <Cell className={styles.cell}>
                    <span className={styles.darkCell}>
                      {b.totalRate === null ? formatPrice(0) : formatPrice(b.totalRate)}
                    </span>
                  </Cell>
                  <Cell className={styles.cell}>
                    <span
                      className={styles.darkCell}
                      style={{ color: getStatusColor(b.status) }}
                    >
                      {transactionStatusTypes[b.status]}
                    </span>
                    {b.cancellationDate && (
                      <p className={styles.smallDate}>
                        {moment(b.cancellationDate).format('MMM DD, YYYY')}
                      </p>
                    )}
                  </Cell>
                  <Cell className={styles.cell}>
                    <button
                      className={styles.tableButton}
                      onClick={() => openDetails(b.subscriberHotelBookingId)}
                    >
                      Details
                    </button>
                  </Cell>
                </Row>
              ))}
            </Table>
          </div>
          <GuestDetails {...guest} onClose={null} />
        </div>
      </ConfirmationDialog>
    </>
  )
}
export default BookingHistory
